import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { useBeta } from '../../contexts/BetaContext';
import { useBetaData } from '../../contexts/BetaDataContext';
import { useToast } from '../common/Toast';
import { useConfirm } from '../common/ConfirmDialog';
import {
  getWorkItemGroups, createWorkItemGroup,
  getWorkItems, getMyWorkItems, searchWorkItems, createWorkItem, updateWorkItem, buildWorkItemTree, getWorkItemByNo,
  updateWorkItemGroup, deleteWorkItemGroup, getWorkItemGroupTotals, upsertWorkItemView,
  updateWorkItemsStatus, setWorkItemsOwner, createWorkItemDeliveryLogs,
} from '../../api/workItems';
import { getAllCompanies } from '../../api/companies';
import { getTextColorForBg } from '../../utils/textUtils';
import { parseWorkItemRef, clearWorkItemRef, hasWorkItemRef } from '../../utils/workItemUrlParams';
import { canManageWorkItemGroup } from '../../middleware/betaPermissions';
import {
  WORK_ITEM_TYPES, WORK_ITEM_TYPE_LABELS, WORK_ITEM_TYPE_COLORS, isWorkItemNoSearch, parseWorkItemNoSearch,
  WORK_ITEM_PRIORITY_META, WORK_ITEM_ROLE_COLORS,
} from '../../constants/workItem';
import WorkItemModal from './WorkItemModal';
import WorkItemCalendar from './WorkItemCalendar';
import WorkItemBoard from './WorkItemBoard';
import WorkItemBatchBar from './WorkItemBatchBar';
import WorkItemDetailDrawer from './WorkItemDetailDrawer';
import TicketFilterBar from '../beta/filters/TicketFilterBar';
import './WorkItemApp.scss';

const DEFAULT_PRIORITIES = [0, 1, 2, 3, 4];
const SEARCH_PAGE_SIZE = 20;

// 列表內就地新增子項:只打標題(比照詳細內頁的 function list)
const SubAddRow = ({ onSubmit }) => {
  const [val, setVal] = useState('');
  return (
    <div className="wi-addsub">
      <input
        value={val}
        placeholder="＋ 新增子項…"
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            const t = val.trim();
            if (t) { onSubmit(t); setVal(''); }
          }
        }}
      />
    </div>
  );
};

// 依個人排序(id 陣列)重排;不在清單的維持原本(DB fallback)順序殿後
const applyPersonalOrder = (arr, orderIds) => {
  if (!orderIds || !orderIds.length) return arr;
  const pos = new Map(orderIds.map((id, i) => [id, i]));
  return [...arr].sort((a, b) => (pos.has(a.id) ? pos.get(a.id) : 1e9) - (pos.has(b.id) ? pos.get(b.id) : 1e9));
};

// 全系統查詢表頭排序指示(↑/↓/↕,比照 TicketTable.SortIcon)
const SearchSortIcon = ({ field, sortBy, sortOrder }) => (
  <span className={`wi-sort-icon${sortBy === field ? ' active' : ''}`}>
    {sortBy === field ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
  </span>
);

const UNGROUPED = '__ungrouped__';
const GROUP_ACCENTS = ['#4f46e5', '#17b26a', '#f79009', '#7a5af8', '#0e7090', '#dd2590'];
const AVA_COLORS = ['#dd2590', '#6938ef', '#0e7090', '#039855', '#b54708', '#4f46e5'];
const avaColor = (id) => AVA_COLORS[[...String(id || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length];

const WorkItemApp = ({ projectId: propProjectId, createNonce = 0, currentView }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { updateBetaSetting } = useBeta();
  const { projects, companyUsers, statusOptions } = useBetaData();
  const statusMeta = (v) => (statusOptions || []).find((s) => s.value === v) || { value: v, label: v, color: '#94a3b8' };
  const toast = useToast();
  const { confirm } = useConfirm();

  const companyId = viewingCompanyId || profile?.company_id;
  const departmentId = profile?.department_id;
  const canManageGroups = canManageWorkItemGroup(profile);

  // 我的任務 → 跨專案「我的工作項目」;全域搜尋 → 全系統查詢;否則跟著左側選的專案
  const isMine = currentView === 'my-tasks';
  const isSearch = currentView === 'global-search';
  const isCalendar = currentView === 'calendar';
  const isBoard = currentView === 'board'; // 新版看板檢視(尚未實作,先顯示 placeholder)
  const projectId = propProjectId || null;

  const [myItems, setMyItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // 我的工作項目:批次多選
  const [batchMode, setBatchMode] = useState(false); // 批次模式:開啟才顯示勾選框(平時隱藏,免與子項「完成」勾混淆)
  const [myGroups, setMyGroups] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  // 我的工作項目篩選(獨立 state;跨專案故不含群組維度)
  const [mineSearch, setMineSearch] = useState('');
  const [mineStatuses, setMineStatuses] = useState([]);
  const [minePriorities, setMinePriorities] = useState([...DEFAULT_PRIORITIES]);
  const [mineAssignees, setMineAssignees] = useState([]);
  const [mineTypes, setMineTypes] = useState([...WORK_ITEM_TYPES]);
  const [mineProjects, setMineProjects] = useState([]); // 我的工作項目:專案篩選(inclusive,預設全選)
  // 全系統查詢:server-side 分頁平表(比照原始 Ticket 查詢),每列顯示 專案 + 群組
  const [searchTerm, setSearchTerm] = useState('');
  const [searchStatuses, setSearchStatuses] = useState([]);
  const [searchPriorities, setSearchPriorities] = useState([...DEFAULT_PRIORITIES]);
  const [searchAssignees, setSearchAssignees] = useState([]);
  const [searchProjects, setSearchProjects] = useState([]);
  const [searchTypes, setSearchTypes] = useState([...WORK_ITEM_TYPES]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchCount, setSearchCount] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchLoading, setSearchLoading] = useState(false);
  // 表頭排序(比照 Ticket 全系統查詢;預設編號新→舊,穩定不跳頁)
  const [searchSortBy, setSearchSortBy] = useState('work_item_no');
  const [searchSortOrder, setSearchSortOrder] = useState('desc');
  const [searchRefreshKey, setSearchRefreshKey] = useState(0); // Drawer 改群組/專案後靜默重抓當前頁
  const searchSilentRef = useRef(false); // 靜默重抓:不閃「載入中…」
  // 過往工作項目(只查 closed):原地切換,server-side 分頁;專案內 = 該專案,我的 = 指派給我。
  // closed 會無限累積,所以不放在一般篩選的狀態選項裡,一律走這裡分頁查。篩選格式比照所在畫面(去掉狀態)。
  const [closedMode, setClosedMode] = useState(false);
  const [closedSearch, setClosedSearch] = useState('');
  const [closedPriorities, setClosedPriorities] = useState([...DEFAULT_PRIORITIES]);
  const [closedTypes, setClosedTypes] = useState([...WORK_ITEM_TYPES]);
  const [closedAssignees, setClosedAssignees] = useState([]);
  const [closedGroups, setClosedGroups] = useState([]); // 專案內:群組篩選(進入時預設全選)
  const [closedProjects, setClosedProjects] = useState([]); // 我的:專案篩選(進入時預設全選)
  const [closedResults, setClosedResults] = useState([]);
  const [closedCount, setClosedCount] = useState(0);
  const [closedPage, setClosedPage] = useState(1);
  const [closedLoading, setClosedLoading] = useState(false);
  const [closedSortBy, setClosedSortBy] = useState('work_item_no');
  const [closedSortOrder, setClosedSortOrder] = useState('desc');
  const [closedRefreshKey, setClosedRefreshKey] = useState(0);
  const closedSilentRef = useRef(false);

  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [groupTotals, setGroupTotals] = useState({}); // 各群組含所有狀態的總筆數(判斷該不該隱藏空群組)
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [modalState, setModalState] = useState(null); // { mode:'create'|'edit', parentId?, groupId?, item? }
  const [newGroupName, setNewGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [dragGroupId, setDragGroupId] = useState(null);
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  // 群組個人排序:存 project_profiles.settings.wi_group_order[projectId](跨裝置,沿用既有 per-user 設定機制)
  const groupOrderForProject = projectId ? (profile?.settings?.wi_group_order?.[projectId] || null) : null;
  const saveGroupOrder = (ids) => {
    if (!projectId) return;
    const cur = profile?.settings?.wi_group_order || {};
    updateBetaSetting('wi_group_order', { ...cur, [projectId]: ids });
  };
  const [drawerItemId, setDrawerItemId] = useState(null);
  const [drawerSubId, setDrawerSubId] = useState(null); // 點列表子項時,開母項 drawer 並自動彈出該子項 card
  const [searchParams, setSearchParams] = useSearchParams();
  const closeDrawer = () => {
    setDrawerItemId(null);
    setDrawerSubId(null);
    // 從網址列(而非可能落後的 router state)讀,並整組清乾淨:wi / wino / wisub,
    // cid 若還被 Ticket deep-link 用著就留下。殘留的 cid 正是「開到別張」的根源。
    const sp = new URLSearchParams(window.location.search);
    if (hasWorkItemRef(sp)) setSearchParams(clearWorkItemRef(sp), { replace: true });
  };
  // 點列表子項:開「母項」drawer + 指定要彈出的子項
  const openSubItemInList = (sub) => { setDrawerSubId(sub.id); setDrawerItemId(sub.parent_id); };

  // ── 專案列表篩選(client-side;比照 TicketFilterBar 的 inclusive/exclusive 模式)──
  // 專案內 / 我的工作項目的狀態篩選不提供 closed(過往改走「過往工作項目」分頁查詢)
  const activeStatusOptions = useMemo(() => (statusOptions || []).filter((s) => s.value !== 'closed'), [statusOptions]);
  const defaultStatuses = useMemo(() => activeStatusOptions.map((s) => s.value), [activeStatusOptions]);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterPriorities, setFilterPriorities] = useState([...DEFAULT_PRIORITIES]);
  const [filterAssignees, setFilterAssignees] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]); // 群組篩選(inclusive 預設全選;切專案 reset)
  const [filterTypes, setFilterTypes] = useState([...WORK_ITEM_TYPES]); // 類型篩選(inclusive 預設全選)
  // statusOptions 非同步載入後設定預設(僅首次)
  const statusInitRef = useRef(false);
  useEffect(() => {
    if (statusInitRef.current) return;
    if (defaultStatuses.length > 0) { statusInitRef.current = true; setFilterStatuses(defaultStatuses); setMineStatuses(defaultStatuses); }
  }, [defaultStatuses]);
  // 我的工作項目專案篩選:projects 載入後預設全選(僅首次)
  const mineProjInitRef = useRef(false);
  useEffect(() => {
    if (mineProjInitRef.current) return;
    if ((projects || []).length > 0) { mineProjInitRef.current = true; setMineProjects(projects.map((p) => p.id)); }
  }, [projects]);

  // 全系統查詢的「全部狀態」集合(判斷是否全不勾用;宣告在這裡是因為下方 effect 要用)
  const searchAllStatuses = useMemo(() => (statusOptions || []).map((s) => s.value), [statusOptions]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const [g, i] = await Promise.all([
      getWorkItemGroups(projectId, { companyId }),
      getWorkItems(projectId, { companyId }),
    ]);
    const gl = g.data || [];
    setGroups(gl);
    setItems(i.data || []);
    const { data: totals } = await getWorkItemGroupTotals(projectId, gl.map((x) => x.id), { companyId });
    setGroupTotals(totals || {});
    setLoading(false);
  }, [projectId, companyId]);

  useEffect(() => { if (!isMine) load(); }, [load, isMine]);

  const loadMine = useCallback(async () => {
    if (!isMine || !profile?.id) return;
    setMyLoading(true);
    const [mi, g] = await Promise.all([
      getMyWorkItems(profile.id, { companyId }),
      getWorkItemGroups(null, { companyId }),
    ]);
    setMyItems(mi.data || []);
    setMyGroups(g.data || []);
    setMyLoading(false);
  }, [isMine, profile?.id, companyId]);
  useEffect(() => { loadMine(); }, [loadMine]);

  // 我的工作項目:預設展開有子項的母項(讓指派給我的子項直接可見)
  useEffect(() => {
    if (!isMine) return;
    const pids = new Set(myItems.filter((i) => i.parent_id).map((i) => i.parent_id));
    if (pids.size) setExpanded((prev) => new Set([...prev, ...pids]));
  }, [isMine, myItems]);

  // 全系統狀態預設含 closed(全部);專案預設全選;僅首次
  const searchStatusInitRef = useRef(false);
  useEffect(() => {
    if (searchStatusInitRef.current) return;
    const all = (statusOptions || []).map((s) => s.value);
    if (all.length > 0) { searchStatusInitRef.current = true; setSearchStatuses(all); }
  }, [statusOptions]);
  const searchProjInitRef = useRef(false);
  useEffect(() => {
    if (searchProjInitRef.current) return;
    if ((projects || []).length > 0) { searchProjInitRef.current = true; setSearchProjects(projects.map((p) => p.id)); }
  }, [projects]);

  // 篩選變動 → 回第一頁
  useEffect(() => {
    setSearchPage(1);
  }, [searchTerm, searchStatuses, searchPriorities, searchAssignees, searchProjects]);

  // 全系統查詢:server-side 分頁(關鍵字 debounce)
  useEffect(() => {
    if (!isSearch) return;
    // inclusive 篩選只要有一項「全不勾」就是沒結果,不能送給 API —— API 那邊是
    // `if (xs.length) q.in(...)`,空陣列會被當成不篩選,結果會變成跟全勾一樣。
    // 狀態要等 statusOptions 載入(searchStatuses 在那之前必然是空)。
    const noneSelected = ((projects || []).length > 0 && searchProjects.length === 0)
      || (searchAllStatuses.length > 0 && searchStatuses.length === 0)
      || searchPriorities.length === 0
      || searchTypes.length === 0;
    if (noneSelected) {
      setSearchResults([]); setSearchCount(0); setSearchLoading(false);
      return;
    }
    if (!searchSilentRef.current) setSearchLoading(true);
    const t = setTimeout(async () => {
      const { data, count } = await searchWorkItems({
        companyId, search: searchTerm, statuses: searchStatuses, priorities: searchPriorities, types: searchTypes,
        assigneeIds: searchAssignees, projectIds: searchProjects, page: searchPage, pageSize: SEARCH_PAGE_SIZE,
        viewerUserId: profile?.id, sortBy: searchSortBy, sortOrder: searchSortOrder,
      });
      setSearchResults(data);
      setSearchCount(count);
      setSearchLoading(false);
      searchSilentRef.current = false;
    }, 250);
    return () => clearTimeout(t);
  }, [isSearch, companyId, searchTerm, searchStatuses, searchPriorities, searchTypes, searchAssignees, searchProjects, searchPage, searchSortBy, searchSortOrder, searchRefreshKey, projects, searchAllStatuses]);

  // topbar「新增工作項目」觸發:只在 nonce「變化」時開,避免切版 remount 時用舊值誤開
  const lastCreateNonce = useRef(createNonce);
  useEffect(() => {
    if (createNonce !== lastCreateNonce.current) {
      lastCreateNonce.current = createNonce;
      if (createNonce > 0) setModalState({ mode: 'create' });
    }
  }, [createNonce]);

  // deep-link:URL 指到哪個項目就自動開該 drawer(複製連結 / 指派通知 / 日報點擊)。
  // 參數語義由 utils/workItemUrlParams 統一判讀,這裡只負責「解出 id → 開 drawer」。
  // wisub:母項 drawer 開啟後彈出該子項的輕量 card(DailyReport/通知點子項)。
  const wiParam = searchParams.get('wi');
  const winoParam = searchParams.get('wino');
  const wiSubParam = searchParams.get('wisub');
  const cidParam = searchParams.get('cid');
  // 網址原本指著某個項目、現在不指了(切專案 / 開 Ticket / 上一頁)→ 關掉 drawer。
  // 只在「曾經有 → 變成沒有」時關,避免點列表開的 drawer(不經網址)被無關的參數變動關掉。
  const hadUrlRef = useRef(false);
  useEffect(() => {
    const ref = parseWorkItemRef(searchParams);
    if (!ref) {
      if (hadUrlRef.current) { hadUrlRef.current = false; setDrawerItemId(null); setDrawerSubId(null); }
      return undefined;
    }
    hadUrlRef.current = true;
    if (ref.subId != null) setDrawerSubId(ref.subId);
    if (ref.by === 'id') {
      setDrawerItemId(ref.id);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      // cid slug → company_id(mirror Ticket 的 byName)。解不到公司(無權限 / cid 打錯)= 不 fallback 亂開。
      let target = null;
      try {
        const { data: companies } = await getAllCompanies();
        target = (companies || []).find((c) => c.name?.toLowerCase().trim() === (ref.cid || '').toLowerCase().trim())?.id || null;
      } catch { /* target 保持 null */ }
      // 解到公司才查編號。注意:work_items 目前沒開 RLS,這裡沒有伺服器端的公司隔離,
      // 唯一的門檻是 cid 要對得上一間存在的公司(見 265 migration 註記的待確認事項)。
      const { data } = target ? await getWorkItemByNo(ref.no, target) : { data: null };
      if (cancelled) return;
      if (data?.id) {
        setDrawerItemId(data.id);
      } else {
        // 公司解不到 / 無權限 / 編號不存在 → 不退回當 id 亂開,關掉 + 提示(避免開到錯項目或卡在載入中)
        setDrawerItemId(null);
        toast.error('找不到該工作項目,或你沒有權限查看');
      }
    })();
    return () => { cancelled = true; };
  }, [wiParam, winoParam, wiSubParam, cidParam, companyId]);

  const tree = useMemo(() => buildWorkItemTree(items), [items]);

  // group_id → 頂層 work_items
  const rootsByGroup = useMemo(() => {
    const map = {};
    tree.forEach((node) => {
      // 只放「真頂層」(parent_id 為 null)。孤兒子項(父項不在當前集合,
      // 例如父已 closed 被 server 濾掉)不該浮成頂層掉進未分組;closed 父項改從「過往工作項目」查。
      if (node.parent_id) return;
      const key = node.group_id || UNGROUPED;
      (map[key] ||= []).push(node);
    });
    return map;
  }, [tree]);

  // ── 篩選判斷 + 套用到頂層 work_items ──
  const matchNode = useCallback((n) => {
    const term = filterSearch.trim();
    if (term) {
      // 規則與伺服器端 searchWorkItems / 舊 Ticket 一致:# 才是查編號,純數字走文字搜尋
      if (isWorkItemNoSearch(term)) {
        if (n.work_item_no !== parseWorkItemNoSearch(term)) return false;
      } else {
        const t = term.toLowerCase();
        if (!(n.title || '').toLowerCase().includes(t) && !(n.description || '').toLowerCase().includes(t)) return false;
      }
    }
    // inclusive:全不勾 = 空結果,不是「不篩選 = 全部」。
    // 狀態的預設值是等 statusOptions 非同步載入後才填的,那之前 filterStatuses 必然是空,
    // 所以要用 defaultStatuses.length 當守門 —— 否則載入那一瞬間畫面會整個空白閃一下。
    // 優先度與類型是同步初始化成完整集合,不需要守門。
    if (defaultStatuses.length > 0 && !filterStatuses.includes(n.status)) return false;
    if (!filterPriorities.includes(n.priority ?? 0)) return false;
    if (!filterTypes.includes(n.type)) return false;
    // 指派人是 additive picker(沒有全選概念),空 = 不限,維持原本語意
    if (filterAssignees.length && !(n.assignees || []).some((a) => filterAssignees.includes(a.user_id))) return false;
    return true;
  }, [filterSearch, filterStatuses, filterPriorities, filterAssignees, filterTypes, defaultStatuses]);

  const displayRootsByGroup = useMemo(() => {
    const map = {};
    Object.entries(rootsByGroup).forEach(([k, arr]) => { map[k] = arr.filter(matchNode); });
    return map;
  }, [rootsByGroup, matchNode]);

  // 群組個人排序(工作項目本身不排序)
  const orderedGroups = useMemo(() => applyPersonalOrder(groups, groupOrderForProject), [groups, groupOrderForProject]);

  const isStatusCustomized = filterStatuses.length > 0 && (
    filterStatuses.length !== defaultStatuses.length || !defaultStatuses.every((v) => filterStatuses.includes(v))
  );
  const isPriorityCustomized = filterPriorities.length !== DEFAULT_PRIORITIES.length
    || !DEFAULT_PRIORITIES.every((v) => filterPriorities.includes(v));
  // 群組篩選選項(orderedGroups + 未分組)；inclusive 預設全選
  const groupFilterOptions = useMemo(() => [...orderedGroups.map((g) => ({ id: g.id, name: g.name })), { id: UNGROUPED, name: '未分組' }], [orderedGroups]);
  const typeOptions = useMemo(() => WORK_ITEM_TYPES.map((t) => ({ value: t, label: WORK_ITEM_TYPE_LABELS[t], color: WORK_ITEM_TYPE_COLORS[t].fg })), []);
  const isGroupCustomized = filterGroups.length > 0 && filterGroups.length < groupFilterOptions.length;
  const isTypeCustomized = filterTypes.length < WORK_ITEM_TYPES.length;
  const activeFilterCount = (isStatusCustomized ? 1 : 0) + (isPriorityCustomized ? 1 : 0)
    + (filterAssignees.length ? 1 : 0) + (filterSearch.trim() ? 1 : 0) + (isGroupCustomized ? 1 : 0) + (isTypeCustomized ? 1 : 0);
  const clearFilters = () => {
    setFilterSearch(''); setFilterStatuses([...defaultStatuses]);
    setFilterPriorities([...DEFAULT_PRIORITIES]); setFilterAssignees([]);
    setFilterGroups(groupFilterOptions.map((g) => g.id)); setFilterTypes([...WORK_ITEM_TYPES]);
  };
  // 切專案(groups 變)→ 群組篩選預設全選
  useEffect(() => { setFilterGroups([...(groups || []).map((g) => g.id), UNGROUPED]); }, [groups]);

  // ── 過往工作項目(只查 closed,server-side 分頁)──
  // 切專案 / 切畫面 → 回到活躍列表
  useEffect(() => { setClosedMode(false); }, [projectId, currentView]);
  const enterClosedMode = () => {
    setClosedSearch(''); setClosedPriorities([...DEFAULT_PRIORITIES]); setClosedTypes([...WORK_ITEM_TYPES]);
    setClosedAssignees([]); setClosedGroups(groupFilterOptions.map((g) => g.id)); setClosedProjects((projects || []).map((p) => p.id));
    setClosedSortBy('work_item_no'); setClosedSortOrder('desc'); setClosedPage(1);
    setClosedResults([]); setClosedCount(0);
    setClosedMode(true);
  };
  const closedScopeMine = isMine;
  useEffect(() => { setClosedPage(1); }, [closedSearch, closedPriorities, closedTypes, closedAssignees, closedGroups, closedProjects]);
  useEffect(() => {
    if (!closedMode) return;
    if (!closedScopeMine && !projectId) return;
    if (closedScopeMine && !profile?.id) return;
    // inclusive 篩選全不勾 = 空結果(API 端空陣列 = 不篩,所以要在送出前擋)
    const noneSelected = closedPriorities.length === 0 || closedTypes.length === 0
      || (!closedScopeMine && closedGroups.length === 0)
      || (closedScopeMine && (projects || []).length > 0 && closedProjects.length === 0);
    if (noneSelected) { setClosedResults([]); setClosedCount(0); setClosedLoading(false); return; }
    // 群組全選 = 不篩(免得 id 清單塞進 URL)
    const allGroupsOn = groupFilterOptions.every((g) => closedGroups.includes(g.id));
    if (!closedSilentRef.current) setClosedLoading(true);
    const t = setTimeout(async () => {
      const { data, count } = await searchWorkItems({
        companyId, search: closedSearch, statuses: ['closed'], priorities: closedPriorities, types: closedTypes,
        assigneeIds: closedAssignees,
        projectIds: closedScopeMine ? closedProjects : [projectId],
        groupIds: closedScopeMine || allGroupsOn ? [] : closedGroups,
        requireAssigneeId: closedScopeMine ? profile.id : null,
        page: closedPage, pageSize: SEARCH_PAGE_SIZE, viewerUserId: profile?.id,
        sortBy: closedSortBy, sortOrder: closedSortOrder,
      });
      setClosedResults(data);
      setClosedCount(count);
      setClosedLoading(false);
      closedSilentRef.current = false;
    }, 250);
    return () => clearTimeout(t);
  }, [closedMode, closedScopeMine, projectId, companyId, profile?.id, projects, groupFilterOptions, closedSearch, closedPriorities, closedTypes, closedAssignees, closedGroups, closedProjects, closedPage, closedSortBy, closedSortOrder, closedRefreshKey]);
  const handleClosedSort = (field) => {
    if (closedSortBy === field) setClosedSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setClosedSortBy(field); setClosedSortOrder('desc'); }
    setClosedPage(1);
  };

  // ── 群組拖曳排序(個人排序,存 project_profiles.settings;僅專案內視圖、未篩選可拖;不影響他人)──
  const dndEnabled = activeFilterCount === 0 && !isMine && !isSearch;

  const handleGroupDrop = (targetId) => {
    const from = dragGroupId; setDragGroupId(null); setDragOverGroupId(null);
    if (!from || from === targetId) return;
    const ids = orderedGroups.map((g) => g.id);
    const fi = ids.indexOf(from);
    const ti = ids.indexOf(targetId);
    if (fi < 0 || ti < 0) return;
    const [moved] = ids.splice(fi, 1);
    ids.splice(ti, 0, moved);
    saveGroupOrder(ids);
  };


  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAddGroup = async () => {
    const name = newGroupName.trim();
    if (!name) { setAddingGroup(false); return; }
    const { data, error } = await createWorkItemGroup({ project_id: projectId, company_id: companyId, department_id: departmentId, name });
    if (error) { toast.error('建立群組失敗'); return; }
    setGroups((prev) => [...prev, data]);
    setNewGroupName('');
    setAddingGroup(false);
    toast.success('已建立群組');
  };

  const handleDeleteGroup = async (group) => {
    const roots = rootsByGroup[group.id] || [];
    if (roots.length > 0) {
      toast.error(`「${group.name}」底下還有 ${roots.length} 個工作項目,請先移動或關閉再刪除群組`);
      return;
    }
    const ok = await confirm({
      title: '刪除群組',
      message: `確定刪除空群組「${group.name}」?`,
      confirmText: '刪除',
      type: 'danger',
    });
    if (!ok) return;
    const { error } = await deleteWorkItemGroup(group.id);
    if (error) { toast.error('刪除群組失敗'); return; }
    setGroups((prev) => prev.filter((g) => g.id !== group.id));
    toast.success('已刪除群組');
  };

  const submitRenameGroup = async (group, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || trimmed === group.name) { setEditingGroupId(null); return; }
    const { error } = await updateWorkItemGroup(group.id, { name: trimmed });
    if (error) { toast.error('群組改名失敗'); return; }
    setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, name: trimmed } : g)));
    setEditingGroupId(null);
  };

  const refresh = () => (isMine ? loadMine() : load());

  // Drawer 內改動的同步:搜尋檢視沒有 load() 可用(projectId=null),row 原地 merge、不重排不跳頁
  const handleDrawerChanged = (updated) => {
    // 過往:重開(改回非 closed)的要從列表消失 → 靜默重抓當前頁;背後的活躍列表也一併刷新
    if (closedMode && !isSearch) {
      closedSilentRef.current = true;
      setClosedRefreshKey((k) => k + 1);
      refresh();
      return;
    }
    if (!isSearch) { refresh(); return; }
    if (!updated) return; // 子項/留言/時數等:列表欄位不受影響
    const row = searchResults.find((r) => r.id === updated.id);
    if (!row) return; // 不在當前頁(如 deep-link 開的 drawer)
    // 群組/專案被改 → join 出來的名稱會過時,改走靜默重抓當前頁
    if (updated.group_id !== row.group_id || updated.project_id !== row.project_id) {
      searchSilentRef.current = true;
      setSearchRefreshKey((k) => k + 1);
      return;
    }
    // updateWorkItem 回傳無 join key(projects/group/assignees)不會蓋掉;getWorkItem 帶的 join 是新值
    setSearchResults((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };

  // 表頭排序:同欄再點 = 翻轉,換欄 = 重設 desc;都回第一頁(比照 useTicketSearch.handleSort)
  const handleSearchSort = (field) => {
    if (searchSortBy === field) setSearchSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSearchSortBy(field); setSearchSortOrder('desc'); }
    setSearchPage(1);
  };

  // 列表就地新增子項(只帶標題,建立者自動當負責人)
  const addSubFromList = async (node, title) => {
    const { error } = await createWorkItem({
      title, type: 'task', project_id: node.project_id, parent_id: node.id, group_id: node.group_id,
      company_id: companyId, department_id: departmentId, creator_id: profile?.id,
      assignee_ids: profile?.id ? [profile.id] : [],
    });
    if (error) { toast.error('新增子項失敗'); return; }
    refresh();
  };

  const usersById = useMemo(() => {
    const m = new Map();
    (companyUsers || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [companyUsers]);

  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clearSelect = () => setSelectedIds(new Set());
  // 批次:對選中的 N 條各自套用(狀態 / Owner / 交付紀錄)
  const applyBatchStatus = async (status) => {
    const ids = [...selectedIds];
    await updateWorkItemsStatus(ids, status, profile?.id);
    await loadMine(); clearSelect(); toast.success(`已更新 ${ids.length} 項狀態`);
  };
  const applyBatchOwner = async (userId) => {
    const ids = [...selectedIds];
    await setWorkItemsOwner(ids, userId, profile?.id);
    await loadMine(); clearSelect(); toast.success(`已改 ${ids.length} 項 Owner`);
  };
  const applyBatchDelivery = async (d) => {
    const ids = [...selectedIds];
    const rows = ids.map((id) => ({
      work_item_id: id, user_id: profile?.id, label: d.label,
      version: d.version || null, environment: d.environment || null, note: d.note || null,
      company_id: companyId, department_id: departmentId,
    }));
    await createWorkItemDeliveryLogs(rows);
    await loadMine(); clearSelect(); toast.success(`已為 ${ids.length} 項新增交付紀錄`);
  };

  // 子項無詳情頁可手動改狀態,勾選完成/取消時同步 status(closed/open),避免母項關閉後子項狀態卡在 open 一直出現在篩選內
  const handleToggleComplete = async (node) => {
    const next = !node.is_completed;
    const status = next ? 'closed' : 'open';
    const completed_at = next ? new Date().toISOString() : null;
    setItems((prev) => prev.map((i) => (i.id === node.id ? { ...i, is_completed: next, status, completed_at } : i)));
    setMyItems((prev) => prev.map((i) => (i.id === node.id ? { ...i, is_completed: next, status, completed_at } : i)));
    const { error } = await updateWorkItem(node.id, { is_completed: next, status, completed_at });
    if (error) { toast.error('更新失敗'); refresh(); }
  };

  // OWNER = 最早指派者;列表顯示 頭像 + 名稱(其餘人數以 +N 表示)
  const renderAvatars = (assignees) => {
    const list = assignees || [];
    if (!list.length) return <span className="wi-dash">—</span>;
    const owner = [...list].sort((a, b) => new Date(a.assigned_at || 0) - new Date(b.assigned_at || 0))[0];
    const u = usersById.get(owner.user_id);
    const name = u?.name || u?.email || '?';
    return (
      <span className="wi-owner">
        <span className="wi-ava" style={{ background: avaColor(owner.user_id) }} title={name}>
          {u?.avatar_url ? <img src={u.avatar_url} alt={name} /> : name.charAt(0)}
        </span>
        <span className="wi-owner-name">{name}</span>
        {list.length > 1 && <span className="wi-owner-more">+{list.length - 1}</span>}
      </span>
    );
  };

  // 有子項 → 顯示子項時數總和;否則顯示自身
  const hoursText = (node) => {
    const kids = node.children || [];
    if (kids.length) {
      let e = 0; let a = 0; let hasE = false; let hasA = false;
      kids.forEach((c) => {
        if (c.estimated_hours != null) { e += Number(c.estimated_hours); hasE = true; }
        if (c.actual_hours != null) { a += Number(c.actual_hours); hasA = true; }
      });
      if (!hasE && !hasA) return '—';
      return `${hasE ? `${e}h` : '—'} / ${hasA ? `${a}h` : '—'}`;
    }
    if (node.estimated_hours == null && node.actual_hours == null) return '—';
    const e = node.estimated_hours != null ? `${node.estimated_hours}h` : '—';
    return node.actual_hours != null ? `${e} / ${node.actual_hours}h` : e;
  };

  // 最後更新相對時間
  const fmtRelative = (ts) => {
    if (!ts) return '';
    try { return formatDistanceToNow(new Date(ts), { addSuffix: true, locale: zhTW }); } catch { return ''; }
  };
  const today = new Date().toISOString().split('T')[0];

  const renderWorkItemRow = (node, depth = 0) => {
    const hasChildren = node.children?.length > 0;
    const isOpen = expanded.has(node.id);

    // 子項:縮排 + 連接線 + 勾選框(較輕)
    if (depth > 0) {
      const rc = WORK_ITEM_ROLE_COLORS[node.role];
      return (
        <React.Fragment key={node.id}>
          {/* 刻意不在列表提供子項拖曳排序:要調順序一律開 Drawer 的子項 tab。
              列表看到的常是被篩選過的子集,在這裡拖容易讓人誤判影響範圍。 */}
          <div className={`wi-subrow depth-${depth}`}>
            <span className="wi-connector" />
            <button
              className={`wi-check ${node.is_completed ? 'done' : ''}`}
              onClick={() => handleToggleComplete(node)}
              disabled={isMine && batchMode}
              aria-label={node.is_completed ? '標為未完成' : '標為完成'}
            >
              {node.is_completed && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <span className={`wi-subtitle ${node.is_completed ? 'done' : ''}`} onClick={() => openSubItemInList(node)}>
              {node.title}
            </span>
            <span className="wi-col wi-col-role">
              {node.role && <span className="wi-role" style={rc ? { background: rc.bg, color: rc.fg } : undefined}>{node.role}</span>}
            </span>
            <span className="wi-col wi-col-assignee">{renderAvatars(node.assignees)}</span>
            <span className="wi-col wi-col-hours">{hoursText(node)}</span>
            <span className="wi-col wi-col-date">
              {node.end_date
                ? <span className={`wi-duedate ${node.end_date < today && node.status !== 'closed' && node.status !== 'rd_fixed' ? 'overdue' : ''}`}>{node.end_date}</span>
                : <span className="wi-dash">—</span>}
            </span>
            <span className="wi-col wi-col-updated">{node.updated_at ? fmtRelative(node.updated_at) : <span className="wi-dash">—</span>}</span>
            <span className="wi-col wi-col-prio" />
            <span className="wi-col wi-col-type" />
          </div>
          {hasChildren && isOpen && node.children.map((c) => renderWorkItemRow(c, depth + 1))}
        </React.Fragment>
      );
    }

    // work_item(頂層)
    const typeColor = WORK_ITEM_TYPE_COLORS[node.type] || WORK_ITEM_TYPE_COLORS.task;
    const prio = WORK_ITEM_PRIORITY_META[node.priority];
    const sm = statusMeta(node.status);
    return (
      <React.Fragment key={node.id}>
        <div className={`wi-row ${node._context ? 'wi-row--context' : ''}`}>
          {isMine && batchMode && !node._context && (
            <input
              type="checkbox"
              className="wi-sel"
              checked={selectedIds.has(node.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleSelect(node.id)}
            />
          )}
          <button
            className={`wi-chevron ${hasChildren ? '' : 'invisible'} ${isOpen ? 'open' : ''}`}
            onClick={() => hasChildren && toggleExpand(node.id)}
            aria-label={isOpen ? '收合' : '展開'}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 1l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="wi-no">#{node.work_item_no}</span>
          <span className="wi-col wi-col-status">
            <span className="wi-status-pill" style={{ color: getTextColorForBg(sm.color), background: sm.color }}>
              {sm.label}
            </span>
          </span>
          <span className="wi-title" onClick={() => setDrawerItemId(node.id)}>
            {node.title}
          </span>
          {node._context && <span className="wi-context-tag" title="未指派給你,僅作為子項的母項脈絡">脈絡</span>}
          <span className="wi-col wi-col-assignee">{renderAvatars(node.assignees)}</span>
          <span className="wi-col wi-col-hours">{hoursText(node)}</span>
          <span className="wi-col wi-col-date">
            {node.end_date
              ? <span className={`wi-duedate ${node.end_date < today && node.status !== 'closed' && node.status !== 'rd_fixed' ? 'overdue' : ''}`}>{node.end_date}</span>
              : <span className="wi-dash">—</span>}
          </span>
          <span className="wi-col wi-col-updated">{node.updated_at ? fmtRelative(node.updated_at) : <span className="wi-dash">—</span>}</span>
          <span className="wi-col wi-col-prio">
            {prio ? <span className="wi-prio" style={{ color: getTextColorForBg(prio.color), background: prio.color }}>⚑ {prio.label}</span> : <span className="wi-dash">—</span>}
          </span>
          <span className="wi-col wi-col-type">
            <span className="wi-type" style={{ background: typeColor.bg, color: typeColor.fg }}>
              {WORK_ITEM_TYPE_LABELS[node.type] || node.type}
            </span>
          </span>
        </div>
        {hasChildren && isOpen && (
          <>
            {node.children.map((c) => renderWorkItemRow(c, depth + 1))}
            <SubAddRow onSubmit={(t) => addSubFromList(node, t)} />
          </>
        )}
      </React.Fragment>
    );
  };

  const renderGroup = (group, idx) => {
    if (!filterGroups.includes(group.id)) return null; // 群組篩選:未選的群組不顯示
    const roots = displayRootsByGroup[group.id] || [];
    const fullCount = (rootsByGroup[group.id] || []).length;
    const total = groupTotals[group.id] ?? 0;
    // 有項目但目前全不可見(全 closed / 全被篩掉)→ 隱藏整組;真的空(total=0)才保留可管理
    if (roots.length === 0 && total > 0) return null;
    const accent = GROUP_ACCENTS[idx % GROUP_ACCENTS.length];
    return (
      <div
        className={`wi-group ${dragOverGroupId === group.id ? 'wi-group--dragover' : ''}`}
        key={group.id}
        style={{ '--wi-accent': accent }}
        onDragOver={(e) => { if (dragGroupId && dragGroupId !== group.id) { e.preventDefault(); setDragOverGroupId(group.id); } }}
        onDragLeave={() => { if (dragOverGroupId === group.id) setDragOverGroupId(null); }}
        onDrop={() => handleGroupDrop(group.id)}
      >
        <div className="wi-group-header">
          {dndEnabled && (
            <span
              className="wi-group-grip"
              draggable
              onDragStart={() => setDragGroupId(group.id)}
              onDragEnd={() => { setDragGroupId(null); setDragOverGroupId(null); }}
              title="拖曳調整群組順序"
            >⠿</span>
          )}
          {canManageGroups && editingGroupId === group.id ? (
            <input
              className="wi-group-rename"
              autoFocus
              defaultValue={group.name}
              onBlur={(e) => submitRenameGroup(group, e.target.value)}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') setEditingGroupId(null);
              }}
            />
          ) : (
            <span
              className="wi-group-name"
              onDoubleClick={() => canManageGroups && setEditingGroupId(group.id)}
              title={canManageGroups ? '雙擊改名' : undefined}
            >
              {group.name}
            </span>
          )}
          <span className="wi-group-count">{roots.length === fullCount ? `${fullCount} 項` : `${roots.length} / ${fullCount} 項`}</span>
          <button
            className="wi-group-add"
            onClick={() => setModalState({ mode: 'create', groupId: group.id })}
          >
            ＋ 工作項目
          </button>
          {canManageGroups && (
            <>
              <button className="wi-group-rename-btn" onClick={() => setEditingGroupId(group.id)} title="改名">改名</button>
              <button className="wi-group-del" onClick={() => handleDeleteGroup(group)} title="刪除群組">✕</button>
            </>
          )}
        </div>
        {roots.length === 0
          ? <div className="wi-group-empty">{fullCount === 0 ? '此群組尚無工作項目' : '無符合篩選的工作項目'}</div>
          : roots.map((n) => renderWorkItemRow(n, 0))}
      </div>
    );
  };

  // 通用:一組 flat items 依 專案 → 群組 兩層彙整(群組依 sort_order,未分組殿後)
  const groupByProjectAndGroup = (flatItems, allGroups) => {
    const groupById = new Map((allGroups || []).map((g) => [g.id, g]));
    const projMap = new Map();
    (flatItems || []).forEach((it) => {
      const pid = it.project_id;
      if (!projMap.has(pid)) projMap.set(pid, { project: it.projects, byGroup: new Map(), ungrouped: [] });
      const sec = projMap.get(pid);
      if (it.group_id && groupById.has(it.group_id)) {
        if (!sec.byGroup.has(it.group_id)) sec.byGroup.set(it.group_id, []);
        sec.byGroup.get(it.group_id).push(it);
      } else sec.ungrouped.push(it);
    });
    return [...projMap.values()].map((sec) => ({
      project: sec.project,
      total: [...sec.byGroup.values()].reduce((a, arr) => a + arr.length, 0) + sec.ungrouped.length,
      groups: [...sec.byGroup.entries()]
        .map(([gid, gItems]) => ({ group: groupById.get(gid), items: gItems }))
        .sort((a, b) => (a.group.sort_order || 0) - (b.group.sort_order || 0)),
      ungrouped: sec.ungrouped,
    }));
  };

  // 專案 → 群組 → 工作項目 區塊(我的工作項目;比照專案內收合式,母項列 + 子項縮排其下)
  const renderProjectSections = (sections) => sections.map(({ project, total, groups: projGroups, ungrouped: projUngrouped }) => (
    <div className="wi-proj-section" key={project?.id || 'x'} style={{ '--wi-accent': project?.color || '#4f46e5' }}>
      <div className="wi-proj-head">
        <span className="wi-proj-dot" />
        <span className="wi-proj-name">{project?.name || '未知專案'}</span>
        <span className="wi-proj-count">{total} 項</span>
      </div>
      {projGroups.map(({ group, items: gItems }) => (
        <div className="wi-group wi-group--sub" key={group.id}>
          <div className="wi-group-header">
            <span className="wi-group-name">{group.name}</span>
            <span className="wi-group-count">{gItems.length} 項</span>
          </div>
          {gItems.map((n) => renderWorkItemRow(n, 0))}
        </div>
      ))}
      {projUngrouped.length > 0 && (
        <div className="wi-group wi-group--sub">
          <div className="wi-group-header">
            <span className="wi-group-name">未分組</span>
            <span className="wi-group-count">{projUngrouped.length} 項</span>
          </div>
          {projUngrouped.map((n) => renderWorkItemRow(n, 0))}
        </div>
      )}
    </div>
  ));

  // 我的工作項目:建樹後取頂層,依 專案 → 群組 兩層(子項縮排在母項下)
  const myFiltered = useMemo(() => myItems.filter((i) => {
    if (i._context) return true; // 母項脈絡一律保留(供子項樹狀顯示)
    // 規則與伺服器端 searchWorkItems / 舊 Ticket 一致(原本這裡是第三種寫法:
    // 標題子字串 OR 「#編號」子字串,打 39 會連 #139 / #392 一起中)
    if (mineSearch.trim()) {
      const term = mineSearch.trim();
      if (isWorkItemNoSearch(term)) {
        if (i.work_item_no !== parseWorkItemNoSearch(term)) return false;
      } else {
        const t = term.toLowerCase();
        if (!(i.title || '').toLowerCase().includes(t) && !(i.description || '').toLowerCase().includes(t)) return false;
      }
    }
    // inclusive:全不勾 = 空結果(同 matchNode,狀態要等選項載入才生效)
    if (defaultStatuses.length > 0 && !mineStatuses.includes(i.status)) return false;
    if (!minePriorities.includes(i.priority ?? 0)) return false;
    if (!mineTypes.includes(i.type)) return false;
    // 指派人是 additive picker,空 = 不限
    if (mineAssignees.length && !(i.assignees || []).some((a) => mineAssignees.includes(a.user_id))) return false;
    // inclusive:全不勾 = 空結果,不能當成「不篩選 = 全部」(對齊全系統查詢與 Ticket 那邊)。
    // projects 還沒載入時 mineProjects 必然是空的,那時才視為不篩選。
    if ((projects || []).length > 0 && !mineProjects.includes(i.project_id)) return false;
    return true;
  }), [myItems, mineSearch, mineStatuses, minePriorities, mineAssignees, mineTypes, mineProjects, projects, defaultStatuses]);
  const myTree = useMemo(() => buildWorkItemTree(myFiltered), [myFiltered]);
  const mySections = useMemo(() => groupByProjectAndGroup(myTree, myGroups), [myTree, myGroups]);
  const isMineStatusCustomized = mineStatuses.length !== defaultStatuses.length || !defaultStatuses.every((v) => mineStatuses.includes(v));
  const isMineTypeCustomized = mineTypes.length < WORK_ITEM_TYPES.length;
  const mineActiveCount = (isMineStatusCustomized ? 1 : 0)
    + (minePriorities.length !== DEFAULT_PRIORITIES.length || !DEFAULT_PRIORITIES.every((v) => minePriorities.includes(v)) ? 1 : 0)
    + (mineAssignees.length ? 1 : 0) + (mineSearch.trim() ? 1 : 0) + (isMineTypeCustomized ? 1 : 0)
    + ((projects || []).length > 0 && mineProjects.length !== projects.length ? 1 : 0);
  const clearMineFilters = () => { setMineSearch(''); setMineStatuses([...defaultStatuses]); setMinePriorities([...DEFAULT_PRIORITIES]); setMineAssignees([]); setMineTypes([...WORK_ITEM_TYPES]); setMineProjects((projects || []).map((p) => p.id)); };

  const renderMine = () => {
    if (closedMode) return renderClosed();
    if (myLoading) return <div className="wi-loading">載入中…</div>;
    return (
      <div className="wi-list">
        <div className="wi-mine-bar">
          <button
            className={`wi-batch-toggle ${batchMode ? 'on' : ''}`}
            onClick={() => { if (batchMode) clearSelect(); setBatchMode(!batchMode); }}
          >
            {batchMode ? '結束批次編輯' : '批次編輯'}
          </button>
          <button className="wi-closed-toggle" onClick={() => { clearSelect(); setBatchMode(false); enterClosedMode(); }}>
            過往工作項目
          </button>
        </div>
        <TicketFilterBar
          search={mineSearch}
          onSearchChange={setMineSearch}
          statusFilter={mineStatuses}
          onStatusFilterChange={setMineStatuses}
          priorityFilter={minePriorities}
          onPriorityFilterChange={setMinePriorities}
          assigneeFilter={mineAssignees}
          onAssigneeFilterChange={setMineAssignees}
          projects={projects || []}
          projectFilter={mineProjects}
          onProjectFilterChange={setMineProjects}
          types={typeOptions}
          typeFilter={mineTypes}
          onTypeFilterChange={setMineTypes}
          activeFilterCount={mineActiveCount}
          onClearAll={clearMineFilters}
          statusOptions={activeStatusOptions}
          companyUsers={companyUsers}
          searchPlaceholder="搜尋標題或說明…（#編號 搜尋指定項目）"
        />
        {mySections.length === 0 ? (
          <div className="wi-empty">
            <div className="wi-empty-icon">✅</div>
            <h2>{mineActiveCount > 0 ? '沒有符合篩選條件的工作項目' : '目前沒有指派給你的工作項目'}</h2>
            <p>被指派後會出現在這裡(跨專案彙整)。</p>
          </div>
        ) : renderProjectSections(mySections)}
      </div>
    );
  };

  // 全系統查詢:server-side 分頁平表(比照原始 Ticket 查詢)
  const searchActiveCount = (searchStatuses.length !== searchAllStatuses.length ? 1 : 0)
    + (searchPriorities.length !== DEFAULT_PRIORITIES.length ? 1 : 0)
    + (searchAssignees.length ? 1 : 0)
    + (searchProjects.length !== (projects || []).length ? 1 : 0)
    + (searchTypes.length !== WORK_ITEM_TYPES.length ? 1 : 0)
    + (searchTerm.trim() ? 1 : 0);
  const clearSearchFilters = () => {
    setSearchTerm(''); setSearchStatuses([...searchAllStatuses]);
    setSearchPriorities([...DEFAULT_PRIORITIES]); setSearchAssignees([]); setSearchProjects((projects || []).map((p) => p.id)); setSearchTypes([...WORK_ITEM_TYPES]);
  };

  // 全系統查詢:點開項目 → 開 drawer + 標記為已讀(樂觀更新該列)
  // setRows:該列表自己的 setter(全系統查詢 / 過往各一份)
  const openFlatItem = (n, setRows) => {
    setDrawerItemId(n.id);
    if (profile?.id && n.has_new_activity) {
      upsertWorkItemView(n.id, profile.id);
      setRows((prev) => prev.map((r) => (r.id === n.id ? { ...r, has_new_activity: false } : r)));
    }
  };

  const renderSearchRow = (n, onOpen) => {
    const tc = WORK_ITEM_TYPE_COLORS[n.type] || WORK_ITEM_TYPE_COLORS.task;
    const prio = WORK_ITEM_PRIORITY_META[n.priority];
    const sm = statusMeta(n.status);
    const activityTs = n.last_activity_at || n.updated_at;
    return (
      <tr key={n.id} className="wi-ft-row" onClick={() => onOpen(n)}>
        <td className="wi-ft-no">#{n.work_item_no}</td>
        <td><span className="wi-status-pill" style={{ color: getTextColorForBg(sm.color), background: sm.color }}>{sm.label}</span></td>
        <td className="wi-ft-title">{n.title}</td>
        <td>
          <span className="wi-ft-proj">
            <span className="wi-ft-proj-dot" style={{ background: n.projects?.color || '#667085' }} />
            {n.projects?.name || '—'}
          </span>
        </td>
        <td>{n.group?.name ? <span className="wi-ft-group">{n.group.name}</span> : <span className="wi-dash">未分組</span>}</td>
        <td>{renderAvatars(n.assignees)}</td>
        <td className={`wi-ft-updated${n.has_new_activity ? ' unread' : ''}`}>
          {activityTs ? fmtRelative(activityTs) : <span className="wi-dash">—</span>}
        </td>
        <td>{prio ? <span className="wi-prio" style={{ color: getTextColorForBg(prio.color), background: prio.color }}>⚑ {prio.label}</span> : <span className="wi-dash">—</span>}</td>
        <td><span className="wi-type" style={{ background: tc.bg, color: tc.fg }}>{WORK_ITEM_TYPE_LABELS[n.type] || n.type}</span></td>
      </tr>
    );
  };

  // 平表 + 分頁(全系統查詢 / 過往工作項目共用)
  const renderFlatTable = ({ loading: tLoading, rows, count, page, onPage, sortBy, sortOrder, onSort, onOpen, emptyText = '沒有符合條件的工作項目' }) => {
    if (tLoading) return <div className="wi-loading">載入中…</div>;
    if (rows.length === 0) return <div className="wi-empty"><div className="wi-empty-icon">🔍</div><h2>{emptyText}</h2></div>;
    const totalPages = Math.max(1, Math.ceil(count / SEARCH_PAGE_SIZE));
    const from = count === 0 ? 0 : (page - 1) * SEARCH_PAGE_SIZE + 1;
    const to = Math.min(page * SEARCH_PAGE_SIZE, count);
    const sortTh = (field, label) => (
      <th className="sortable" onClick={() => onSort(field)}>{label} <SearchSortIcon field={field} sortBy={sortBy} sortOrder={sortOrder} /></th>
    );
    return (
      <>
        <div className="wi-ft-wrap">
          <table className="wi-flat-table">
            <thead>
              <tr>
                {sortTh('work_item_no', '#')}
                {sortTh('status', '狀態')}
                <th>標題</th><th>專案</th><th>群組</th><th>指派</th>
                {sortTh('last_activity_at', '最後更新')}
                {sortTh('priority', '優先')}
                {sortTh('type', '類型')}
              </tr>
            </thead>
            <tbody>{rows.map((n) => renderSearchRow(n, onOpen))}</tbody>
          </table>
        </div>
        <div className="wi-pager">
          <button disabled={page <= 1} onClick={() => onPage((p) => Math.max(1, p - 1))}>‹</button>
          <span className="wi-pager-info">第 {from}–{to} / {count} 筆 · 第 {page}/{totalPages} 頁</span>
          <button disabled={page >= totalPages} onClick={() => onPage((p) => Math.min(totalPages, p + 1))}>›</button>
        </div>
      </>
    );
  };

  // 過往工作項目:篩選格式比照所在畫面(專案內帶群組、我的帶專案),狀態固定 closed 故不顯示狀態篩選
  const closedActiveCount = (closedSearch.trim() ? 1 : 0)
    + (closedPriorities.length !== DEFAULT_PRIORITIES.length ? 1 : 0)
    + (closedTypes.length !== WORK_ITEM_TYPES.length ? 1 : 0)
    + (closedAssignees.length ? 1 : 0)
    + (!isMine && closedGroups.length !== groupFilterOptions.length ? 1 : 0)
    + (isMine && (projects || []).length > 0 && closedProjects.length !== projects.length ? 1 : 0);
  const clearClosedFilters = () => {
    setClosedSearch(''); setClosedPriorities([...DEFAULT_PRIORITIES]); setClosedTypes([...WORK_ITEM_TYPES]); setClosedAssignees([]);
    setClosedGroups(groupFilterOptions.map((g) => g.id)); setClosedProjects((projects || []).map((p) => p.id));
  };
  const renderClosed = () => (
    <div className="wi-list">
      <div className="wi-search-head">
        <button className="wi-closed-toggle" onClick={() => setClosedMode(false)}>← 返回</button>
        <span className="wi-search-title">{isMine ? '我的過往工作項目' : '過往工作項目'}</span>
        <span className="wi-search-count">共 {closedCount} 筆</span>
      </div>
      <TicketFilterBar
        search={closedSearch}
        onSearchChange={setClosedSearch}
        hideStatusFilter
        statusFilter={[]}
        onStatusFilterChange={() => {}}
        priorityFilter={closedPriorities}
        onPriorityFilterChange={setClosedPriorities}
        assigneeFilter={closedAssignees}
        onAssigneeFilterChange={setClosedAssignees}
        {...(isMine
          ? { projects: projects || [], projectFilter: closedProjects, onProjectFilterChange: setClosedProjects }
          : { groups: groupFilterOptions, groupFilter: closedGroups, onGroupFilterChange: setClosedGroups })}
        types={typeOptions}
        typeFilter={closedTypes}
        onTypeFilterChange={setClosedTypes}
        activeFilterCount={closedActiveCount}
        onClearAll={clearClosedFilters}
        companyUsers={companyUsers}
        searchPlaceholder="搜尋標題、說明或留言…（#編號 搜尋指定項目）"
      />
      {renderFlatTable({
        loading: closedLoading, rows: closedResults, count: closedCount, page: closedPage, onPage: setClosedPage,
        sortBy: closedSortBy, sortOrder: closedSortOrder, onSort: handleClosedSort,
        onOpen: (n) => openFlatItem(n, setClosedResults),
        emptyText: closedActiveCount > 0 ? '沒有符合條件的過往工作項目' : '還沒有過往(Closed)的工作項目',
      })}
    </div>
  );

  const renderSearch = () => (
    <div className="wi-list">
      <div className="wi-search-head">
        <span className="wi-search-title">全系統工作項目查詢</span>
        <span className="wi-search-count">共 {searchCount} 筆</span>
      </div>
      <TicketFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={searchStatuses}
        onStatusFilterChange={setSearchStatuses}
        priorityFilter={searchPriorities}
        onPriorityFilterChange={setSearchPriorities}
        assigneeFilter={searchAssignees}
        onAssigneeFilterChange={setSearchAssignees}
        projectFilter={searchProjects}
        onProjectFilterChange={setSearchProjects}
        types={typeOptions}
        typeFilter={searchTypes}
        onTypeFilterChange={setSearchTypes}
        activeFilterCount={searchActiveCount}
        onClearAll={clearSearchFilters}
        statusOptions={statusOptions || []}
        companyUsers={companyUsers}
        projects={projects || []}
        searchPlaceholder="搜尋標題、說明或留言…（#編號 搜尋指定項目）"
      />
      {renderFlatTable({
        loading: searchLoading, rows: searchResults, count: searchCount, page: searchPage, onPage: setSearchPage,
        sortBy: searchSortBy, sortOrder: searchSortOrder, onSort: handleSearchSort,
        onOpen: (n) => openFlatItem(n, setSearchResults),
      })}
    </div>
  );

  const ungrouped = displayRootsByGroup[UNGROUPED] || [];
  const isEmpty = projectId && !loading && groups.length === 0 && items.length === 0;

  return (
    <div className="workitem-app">
      <div className="wi-body">
        {isMine && renderMine()}
        {isSearch && renderSearch()}
        {isCalendar && (
          <WorkItemCalendar
            companyId={companyId}
            companyUsers={companyUsers}
            onOpenItem={(item) => {
              // 子項:開母項 drawer + 彈出該子項 card(比照列表點子項);母項:直接開
              if (item.parent_id) { setDrawerSubId(item.id); setDrawerItemId(item.parent_id); }
              else setDrawerItemId(item.id);
            }}
          />
        )}

        {isBoard && (
          <WorkItemBoard
            companyId={companyId}
            companyUsers={companyUsers}
            statusOptions={statusOptions}
            onOpenItem={(it) => setDrawerItemId(it.id)}
          />
        )}

        {!isMine && !isSearch && !isCalendar && !isBoard && !projectId && (
          <div className="wi-empty">
            <div className="wi-empty-icon">📁</div>
            <h2>請先從左側選擇專案</h2>
            <p>工作項目依專案分類。你也可以直接用上方「新增工作項目」,在視窗內指定要建到哪個專案。</p>
            <div className="wi-empty-actions">
              <button className="wi-btn-primary" onClick={() => setModalState({ mode: 'create' })}>＋ 新增工作項目</button>
            </div>
          </div>
        )}

        {!isMine && !isSearch && !isCalendar && projectId && closedMode && renderClosed()}

        {!isMine && !isSearch && !isCalendar && projectId && !closedMode && loading && <div className="wi-loading">載入中…</div>}

        {!isMine && !isSearch && !isCalendar && !closedMode && isEmpty && (
          <div className="wi-empty">
            <div className="wi-empty-icon">🗂️</div>
            <h2>這個專案還沒有工作項目</h2>
            <p>新系統從 0 開始,先建一個群組,再新增工作項目。</p>
            {addingGroup ? (
              <div className="wi-newgroup wi-newgroup--center">
                <input
                  autoFocus
                  value={newGroupName}
                  placeholder="群組名稱…"
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleAddGroup();
                    if (e.key === 'Escape') { setAddingGroup(false); setNewGroupName(''); }
                  }}
                  onBlur={handleAddGroup}
                />
              </div>
            ) : (
              <div className="wi-empty-actions">
                {canManageGroups && <button className="wi-btn-secondary" onClick={() => setAddingGroup(true)}>＋ 新增群組</button>}
                <button className="wi-btn-primary" onClick={() => setModalState({ mode: 'create' })}>＋ 新增工作項目</button>
              </div>
            )}
            {/* 沒有活躍項目也要能翻過往(可能全是 closed) */}
            <button className="wi-closed-toggle wi-closed-toggle--center" onClick={enterClosedMode}>查看過往工作項目</button>
          </div>
        )}

        {!isMine && !isSearch && !isCalendar && projectId && !closedMode && !loading && !isEmpty && (
          <div className="wi-list">
            <div className="wi-filter-row">
            <TicketFilterBar
              search={filterSearch}
              onSearchChange={setFilterSearch}
              statusFilter={filterStatuses}
              onStatusFilterChange={setFilterStatuses}
              priorityFilter={filterPriorities}
              onPriorityFilterChange={setFilterPriorities}
              assigneeFilter={filterAssignees}
              onAssigneeFilterChange={setFilterAssignees}
              groups={groupFilterOptions}
              groupFilter={filterGroups}
              onGroupFilterChange={setFilterGroups}
              types={typeOptions}
              typeFilter={filterTypes}
              onTypeFilterChange={setFilterTypes}
              activeFilterCount={activeFilterCount}
              onClearAll={clearFilters}
              statusOptions={activeStatusOptions}
              companyUsers={companyUsers}
              searchPlaceholder="搜尋標題或說明…（#編號 搜尋指定項目）"
            />
            {/* 未分組快速入口(B):與篩選列同一行,點了只篩未分組,不用滑到底 */}
            {ungrouped.length > 0 && filterGroups.includes(UNGROUPED) && (
              <button className="wi-ungrouped-jump" onClick={() => setFilterGroups([UNGROUPED])}>
                未分組 <span className="wi-ungrouped-jump-n">{ungrouped.length}</span>
              </button>
            )}
            <button className="wi-closed-toggle wi-closed-toggle--filter-row" onClick={enterClosedMode}>過往工作項目</button>
            </div>
            {orderedGroups.map((g, i) => renderGroup(g, i))}

            {/* 未分組 */}
            {ungrouped.length > 0 && filterGroups.includes(UNGROUPED) && (
              <div className="wi-group" style={{ '--wi-accent': '#98a2b3' }}>
                <div className="wi-group-header">
                  <span className="wi-group-name">未分組</span>
                  <span className="wi-group-count">{ungrouped.length} 項</span>
                </div>
                {ungrouped.map((n) => renderWorkItemRow(n, 0))}
              </div>
            )}

            {/* 新增群組(僅管理者) */}
            {!canManageGroups ? null : addingGroup ? (
              <div className="wi-newgroup">
                <input
                  autoFocus
                  value={newGroupName}
                  placeholder="群組名稱…"
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleAddGroup();
                    if (e.key === 'Escape') { setAddingGroup(false); setNewGroupName(''); }
                  }}
                  onBlur={handleAddGroup}
                />
              </div>
            ) : (
              <button className="wi-addgroup" onClick={() => setAddingGroup(true)}>＋ 新增群組</button>
            )}
          </div>
        )}
      </div>

      {isMine && selectedIds.size > 0 && (
        <WorkItemBatchBar
          count={selectedIds.size}
          statusOptions={statusOptions}
          companyUsers={companyUsers}
          onStatus={applyBatchStatus}
          onOwner={applyBatchOwner}
          onDelivery={applyBatchDelivery}
          onClear={clearSelect}
        />
      )}

      {modalState && (
        <WorkItemModal
          state={modalState}
          projectId={projectId}
          projects={projects}
          groups={groups}
          companyUsers={companyUsers}
          companyId={companyId}
          departmentId={departmentId}
          creatorId={profile?.id}
          onClose={() => setModalState(null)}
          onSaved={() => { setModalState(null); refresh(); }}
        />
      )}

      {drawerItemId && (
        <WorkItemDetailDrawer
          itemId={drawerItemId}
          initialSubId={drawerSubId}
          companyUsers={companyUsers}
          groups={groups}
          profile={profile}
          companyId={companyId}
          departmentId={departmentId}
          onClose={closeDrawer}
          onChanged={handleDrawerChanged}
          onOpenItem={(id) => setDrawerItemId(id)}
        />
      )}
    </div>
  );
};

export default WorkItemApp;
